import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { timeOffApi, employeeApi, complianceApi } from '../../services/api';
import { EmptyState, Spinner, Modal, ConfirmModal } from '../../components/shared/index';
import {
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  CalendarDaysIcon,
  TrashIcon,
  PencilSquareIcon,
  GlobeAltIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const LEAVE_TYPES = ['vacation','sick','personal','parental','bereavement','other'];
const ACCRUALS    = ['monthly','quarterly','annual','none'];

const STATUS_STYLES = {
  pending:   'bg-amber-100 text-amber-800 border-amber-200',
  approved:  'bg-green-100 text-green-800 border-green-200',
  rejected:  'bg-red-100   text-red-800   border-red-200',
  cancelled: 'bg-gray-100  text-gray-700  border-gray-200',
};

function StatusBadge({ status }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize', STATUS_STYLES[status] || STATUS_STYLES.pending)}>
      {status}
    </span>
  );
}

function daysInclusive(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.round((e - s) / 86400000) + 1;
}

function RequestModal({ open, onClose, onSubmit, pending, policies }) {
  const [type, setType] = useState('vacation');
  const [policyId, setPolicyId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [notes, setNotes] = useState('');

  const days = daysInclusive(start, end);
  const valid = type && start && end && days > 0;

  const submit = () => {
    onSubmit({
      type,
      policy_id: policyId || null,
      start_date: start,
      end_date: end,
      days_requested: days,
      notes: notes || null,
    });
  };

  const reset = () => {
    setType('vacation'); setPolicyId(''); setStart(''); setEnd(''); setNotes('');
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Request time off" size="md">
      <div className="space-y-4">
        <div>
          <label className="label">Type</label>
          <select className="input" value={type} onChange={e => setType(e.target.value)}>
            {LEAVE_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Policy (optional — drives balance)</label>
          <select className="input" value={policyId} onChange={e => setPolicyId(e.target.value)}>
            <option value="">No policy</option>
            {(policies || []).filter(p => p.type === type).map(p => (
              <option key={p.id} value={p.id}>{p.name} · {p.days_per_year}d/yr</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Start date</label>
            <input type="date" className="input" value={start} onChange={e => setStart(e.target.value)} />
          </div>
          <div>
            <label className="label">End date</label>
            <input type="date" className="input" value={end} onChange={e => setEnd(e.target.value)} />
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          {days > 0 ? `${days} day${days !== 1 ? 's' : ''} requested` : 'Pick valid dates to compute duration.'}
        </div>
        <div>
          <label className="label">Notes</label>
          <textarea className="input h-20 resize-none" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional context for your approver…" />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => { reset(); onClose(); }} className="btn-secondary">Cancel</button>
          <button disabled={!valid || pending} onClick={submit} className="btn-primary disabled:opacity-40">
            {pending ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DecisionModal({ open, onClose, onSubmit, pending, action }) {
  const [notes, setNotes] = useState('');
  return (
    <Modal open={open} onClose={() => { setNotes(''); onClose(); }} title={action === 'approve' ? 'Approve request' : 'Reject request'} size="sm">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">Add a note for the employee (optional).</p>
        <textarea className="input h-24 resize-none" value={notes} onChange={e => setNotes(e.target.value)} />
        <div className="flex justify-end gap-3">
          <button onClick={() => { setNotes(''); onClose(); }} className="btn-secondary">Cancel</button>
          <button disabled={pending} onClick={() => { onSubmit({ decision_notes: notes || null }); setNotes(''); }}
            className={clsx(action === 'approve' ? 'btn-primary' : 'btn-danger', 'disabled:opacity-40')}>
            {pending ? 'Working…' : (action === 'approve' ? 'Approve' : 'Reject')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PolicyModal({ open, onClose, onSubmit, pending, existing }) {
  const [form, setForm] = useState({
    name: '', country: '', type: 'vacation',
    days_per_year: 20, accrual: 'annual',
    carry_over_limit: 5, description: '', is_active: true,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name || '',
        country: existing.country || '',
        type: existing.type || 'vacation',
        days_per_year: existing.days_per_year ?? 20,
        accrual: existing.accrual || 'annual',
        carry_over_limit: existing.carry_over_limit ?? 5,
        description: existing.description || '',
        is_active: existing.is_active ?? true,
      });
    } else {
      setForm({
        name: '', country: '', type: 'vacation',
        days_per_year: 20, accrual: 'annual',
        carry_over_limit: 5, description: '', is_active: true,
      });
    }
  }, [existing]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.name && form.type && form.accrual;

  return (
    <Modal open={open} onClose={onClose} title={existing ? 'Edit policy' : 'New time-off policy'} size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Name</label>
            <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. EU vacation" />
          </div>
          <div>
            <label className="label">Country (ISO-2)</label>
            <input className="input" value={form.country} maxLength={2} onChange={e => set('country', e.target.value.toUpperCase())} placeholder="Optional (GB, US…)" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Type</label>
            <select className="input capitalize" value={form.type} onChange={e => set('type', e.target.value)}>
              {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Accrual</label>
            <select className="input capitalize" value={form.accrual} onChange={e => set('accrual', e.target.value)}>
              {ACCRUALS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Days / year</label>
            <input type="number" step="0.5" min="0" className="input" value={form.days_per_year} onChange={e => set('days_per_year', e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Carry-over limit</label>
            <input type="number" step="0.5" min="0" className="input" value={form.carry_over_limit} onChange={e => set('carry_over_limit', e.target.value)} />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              Active
            </label>
          </div>
        </div>
        <div>
          <label className="label">Description</label>
          <textarea className="input h-20 resize-none" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button disabled={!valid || pending} onClick={() => onSubmit(form)} className="btn-primary disabled:opacity-40">
            {pending ? 'Saving…' : (existing ? 'Save changes' : 'Create policy')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function TimeOffTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ['hr_admin','super_admin'].includes(user?.role);
  const isManager = isAdmin || user?.role === 'manager';

  const [innerTab, setInnerTab] = useState('mine');
  const [openReq, setOpenReq] = useState(false);
  const [decision, setDecision] = useState(null); // {req, action}
  const [policyModal, setPolicyModal] = useState({ open: false, existing: null });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmSeed, setConfirmSeed] = useState(null); // country library card

  // Queries
  const { data: mineData, isLoading: loadingMine } = useQuery({
    queryKey: ['time-off','mine'],
    queryFn:  () => timeOffApi.list({ mine: 1, per_page: 50 }).then(r => r.data),
    enabled:  innerTab === 'mine',
  });

  const { data: teamData, isLoading: loadingTeam } = useQuery({
    queryKey: ['time-off','team'],
    queryFn:  () => timeOffApi.list({ pending_approval: 1, per_page: 50 }).then(r => r.data),
    enabled:  innerTab === 'team' && isManager,
  });

  const { data: policies, isLoading: loadingPolicies } = useQuery({
    queryKey: ['time-off','policies'],
    queryFn:  () => timeOffApi.policies.list().then(r => r.data),
  });

  const { data: countryLibrary, isLoading: loadingCountryLibrary } = useQuery({
    queryKey: ['time-off','country-library'],
    queryFn:  () => complianceApi.countryLibrary().then(r => r.data),
    enabled:  innerTab === 'policies' && isAdmin,
  });

  const { data: balances } = useQuery({
    queryKey: ['leave-balances', user?.employee_id],
    queryFn:  () => timeOffApi.balances(user.employee_id).then(r => r.data),
    enabled:  !!user?.employee_id,
  });

  // Mutations
  const createReq = useMutation({
    mutationFn: timeOffApi.create,
    onSuccess:  () => { toast.success('Request submitted'); setOpenReq(false); qc.invalidateQueries({ queryKey: ['time-off'] }); qc.invalidateQueries({ queryKey: ['leave-balances'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to submit'),
  });

  const decide = useMutation({
    mutationFn: ({ id, action, data }) => action === 'approve' ? timeOffApi.approve(id, data) : timeOffApi.reject(id, data),
    onSuccess:  (_, v) => { toast.success(v.action === 'approve' ? 'Approved' : 'Rejected'); setDecision(null); qc.invalidateQueries({ queryKey: ['time-off'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const savePolicy = useMutation({
    mutationFn: (data) => policyModal.existing
      ? timeOffApi.policies.update(policyModal.existing.id, data)
      : timeOffApi.policies.create(data),
    onSuccess:  () => { toast.success('Policy saved'); setPolicyModal({ open: false, existing: null }); qc.invalidateQueries({ queryKey: ['time-off','policies'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const deletePolicy = useMutation({
    mutationFn: (id) => timeOffApi.policies.delete(id),
    onSuccess:  () => { toast.success('Policy archived'); qc.invalidateQueries({ queryKey: ['time-off','policies'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const seedCountry = useMutation({
    mutationFn: (country) => complianceApi.seedCountryDefaults(country),
    onSuccess:  (res) => {
      const created = res?.data?.created ?? 0;
      toast.success(created > 0 ? `Seeded ${created} policy row${created === 1 ? '' : 's'}` : 'Country defaults are up to date');
      qc.invalidateQueries({ queryKey: ['time-off','policies'] });
      qc.invalidateQueries({ queryKey: ['time-off','country-library'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to seed defaults'),
  });

  const innerTabs = [
    { key: 'mine',     label: 'My requests', roles: ['*'] },
    { key: 'team',     label: 'Team approvals', roles: ['manager','hr_admin','super_admin'] },
    { key: 'policies', label: 'Policies',    roles: ['hr_admin','super_admin'] },
  ].filter(t => t.roles.includes('*') || t.roles.includes(user?.role));

  const rows = (d) => d?.data || d || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1 border-b border-gray-200 flex-1">
          {innerTabs.map(t => (
            <button key={t.key} onClick={() => setInnerTab(t.key)}
              className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                innerTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              {t.label}
            </button>
          ))}
        </div>
        {innerTab !== 'policies' && (
          <button onClick={() => setOpenReq(true)} className="btn-primary ml-4">
            <PlusIcon className="w-4 h-4" /> Request time off
          </button>
        )}
        {innerTab === 'policies' && isAdmin && (
          <button onClick={() => setPolicyModal({ open: true, existing: null })} className="btn-primary ml-4">
            <PlusIcon className="w-4 h-4" /> New policy
          </button>
        )}
      </div>

      {/* Balances bar on my requests */}
      {innerTab === 'mine' && balances?.balances?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          {balances.balances.map(b => (
            <div key={b.id} className="card p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{b.policy?.name || 'Policy'}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {Number(b.available_days).toFixed(1)}<span className="text-sm font-normal text-gray-500"> / {Number(b.accrued_days).toFixed(1)}d</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {Number(b.used_days).toFixed(1)}d used · {Number(b.pending_days).toFixed(1)}d pending
              </p>
            </div>
          ))}
        </div>
      )}

      {/* My requests */}
      {innerTab === 'mine' && (
        loadingMine ? <div className="flex justify-center p-20"><Spinner size="lg" /></div>
        : rows(mineData).length === 0
          ? <EmptyState title="No requests yet" description="When you request time off it'll show up here." />
          : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>{['Type','Dates','Days','Status','Approver','Submitted'].map(h => <th key={h} className="th">{h}</th>)}</tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {rows(mineData).map(r => (
                    <tr key={r.id} className="tr-hover">
                      <td className="td capitalize">{r.type}</td>
                      <td className="td text-sm">{new Date(r.start_date).toLocaleDateString()} → {new Date(r.end_date).toLocaleDateString()}</td>
                      <td className="td">{Number(r.days_requested).toFixed(1)}</td>
                      <td className="td"><StatusBadge status={r.status} /></td>
                      <td className="td text-sm">{r.approver?.name || '—'}</td>
                      <td className="td text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* Team approvals */}
      {innerTab === 'team' && isManager && (
        loadingTeam ? <div className="flex justify-center p-20"><Spinner size="lg" /></div>
        : rows(teamData).length === 0
          ? <EmptyState title="Nothing to approve" description="You're all caught up." />
          : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>{['Employee','Type','Dates','Days','Notes','Actions'].map(h => <th key={h} className="th">{h}</th>)}</tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {rows(teamData).map(r => (
                    <tr key={r.id} className="tr-hover">
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">{r.employee?.name?.[0] ?? '?'}</div>
                          <span className="text-sm font-medium">{r.employee?.name}</span>
                        </div>
                      </td>
                      <td className="td capitalize">{r.type}</td>
                      <td className="td text-sm">{new Date(r.start_date).toLocaleDateString()} → {new Date(r.end_date).toLocaleDateString()}</td>
                      <td className="td">{Number(r.days_requested).toFixed(1)}</td>
                      <td className="td text-xs text-gray-500 max-w-xs truncate">{r.notes || '—'}</td>
                      <td className="td">
                        <div className="flex gap-1">
                          <button onClick={() => setDecision({ req: r, action: 'approve' })}
                            className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Approve">
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDecision({ req: r, action: 'reject' })}
                            className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Reject">
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* Policies */}
      {innerTab === 'policies' && isAdmin && (
        <div className="card p-5 mb-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-2">
                <GlobeAltIcon className="w-4 h-4 text-blue-600" /> Country library
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Statutory leave templates by country. Seed defaults to pre-populate policies scoped to your tenant.
              </p>
            </div>
          </div>

          {loadingCountryLibrary ? (
            <div className="flex justify-center p-8"><Spinner size="md" /></div>
          ) : !countryLibrary || countryLibrary.length === 0 ? (
            <p className="text-sm text-gray-500">No country templates available.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {countryLibrary.map((c) => (
                <div key={c.country} className="border border-gray-200 rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="inline-flex items-center gap-2">
                      <span className="w-8 h-8 bg-blue-50 text-blue-700 rounded-md flex items-center justify-center text-xs font-bold">
                        {c.country}
                      </span>
                      <span className="text-xs text-gray-500">{c.policies} polic{c.policies === 1 ? 'y' : 'ies'}</span>
                    </div>
                    <button
                      onClick={() => setConfirmSeed(c)}
                      disabled={seedCountry.isPending}
                      className="btn-sm btn-secondary"
                      title="Seed defaults"
                    >
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                      Seed
                    </button>
                  </div>
                  {Array.isArray(c.sample) && c.sample.length > 0 && (
                    <ul className="text-xs text-gray-600 space-y-0.5">
                      {c.sample.slice(0, 4).map((s, i) => (
                        <li key={i} className="truncate">• {s}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {innerTab === 'policies' && (
        loadingPolicies ? <div className="flex justify-center p-20"><Spinner size="lg" /></div>
        : (policies || []).length === 0
          ? <EmptyState title="No policies" description="Create a policy to start tracking leave balances." />
          : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>{['Name','Country','Type','Days/yr','Accrual','Carry-over','Active','Actions'].map(h => <th key={h} className="th">{h}</th>)}</tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {policies.map(p => (
                    <tr key={p.id} className="tr-hover">
                      <td className="td font-medium">{p.name}</td>
                      <td className="td"><span className="inline-flex items-center gap-1 text-xs"><CalendarDaysIcon className="w-3 h-3" />{p.country || 'Global'}</span></td>
                      <td className="td capitalize">{p.type}</td>
                      <td className="td">{Number(p.days_per_year).toFixed(1)}</td>
                      <td className="td capitalize">{p.accrual}</td>
                      <td className="td">{Number(p.carry_over_limit).toFixed(1)}</td>
                      <td className="td">{p.is_active ? <span className="text-green-600 text-xs">Active</span> : <span className="text-gray-400 text-xs">Archived</span>}</td>
                      <td className="td">
                        {isAdmin && (
                          <div className="flex gap-1">
                            <button onClick={() => setPolicyModal({ open: true, existing: p })} className="p-1.5 rounded hover:bg-blue-50 text-blue-600">
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                            <button onClick={() => setConfirmDelete(p)} className="p-1.5 rounded hover:bg-red-50 text-red-600">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* Modals */}
      <RequestModal
        open={openReq}
        onClose={() => setOpenReq(false)}
        onSubmit={(d) => createReq.mutate(d)}
        pending={createReq.isPending}
        policies={policies}
      />

      <DecisionModal
        open={!!decision}
        onClose={() => setDecision(null)}
        onSubmit={(d) => decide.mutate({ id: decision.req.id, action: decision.action, data: d })}
        pending={decide.isPending}
        action={decision?.action}
      />

      <PolicyModal
        open={policyModal.open}
        existing={policyModal.existing}
        onClose={() => setPolicyModal({ open: false, existing: null })}
        onSubmit={(d) => savePolicy.mutate(d)}
        pending={savePolicy.isPending}
      />

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deletePolicy.mutate(confirmDelete.id)}
        title="Archive policy?"
        message={`"${confirmDelete?.name}" will be archived and hidden from new requests.`}
        danger
      />

      <ConfirmModal
        open={!!confirmSeed}
        onClose={() => setConfirmSeed(null)}
        onConfirm={() => seedCountry.mutate(confirmSeed.country)}
        title={`Seed ${confirmSeed?.country || ''} defaults?`}
        message={`This will create ${confirmSeed?.policies ?? 0} policy row${confirmSeed?.policies === 1 ? '' : 's'} scoped to your tenant, based on the ${confirmSeed?.country || ''} statutory template. Existing policies are preserved.`}
      />
    </div>
  );
}
